import { StaffListItem } from '@spike/appointments-list-model';
import { AvailableStaffStatus, fetchAvailableStaffThunk } from '@spike/available-staff-action';
import { AvailableStaff } from '@spike/available-staff-model';
import useNonInitialEffect from '@versiondos/hooks';
import { useApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { DropDown } from './DropDown';
import { StaffChip } from './StaffChip';
import styles from './Staff.module.scss';
import { assignAppointmentStaffThunk, BookingsStatus } from '@spike/bookings-action';
import Spinner from 'components/UI/V3/Spinner';
import { status as appointmentStatus } from '@spike/appointment-model';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons';

interface StaffProps {
    appointmentId: number;
    appointmentStatusId: number;
    member: StaffListItem | null;
    scrollerContainerId?: string;
    editable?: boolean;
    className?: string;
    onStaffSaved?: () => void;
}

const noEditableAppointmentStatuses = [
    appointmentStatus.CANCELLED,
    appointmentStatus.COMPLETED,
    appointmentStatus.CANCELLED,
    appointmentStatus.NO_SHOW,
    appointmentStatus.WAITING_PAYMENT_CONFIRMATION,
    appointmentStatus.DECLINED
];

export const Staff: FunctionComponent<StaffProps> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();

    const containerRef = useRef<HTMLDivElement>(null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showDropDown, setShowDropDown] = useState(false);

    const availableStaffByAppointment = useSelector<RootState, Map<number, Array<AvailableStaff>>>(
        state => state.availableStaff.availableStaffForAppointment
    );
    const availableStaffStatus = useSelector<RootState, AvailableStaffStatus>(state => state.availableStaff.status);

    const availableStaff = availableStaffByAppointment.get(props.appointmentId) ?? [];

    const bookingsStatus = useSelector<RootState, BookingsStatus>(state => state.bookings.status);

    const editable = props.editable && !noEditableAppointmentStatuses.includes(props.appointmentStatusId);

    const [coords, setCoords] = useState({ x: 0, y: 0 });

    const updateCoords = () => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setCoords({
                x: rect.x,
                y: rect.y
            });
        }
    };

    useEffect(() => {
        if (containerRef.current) {
            updateCoords();
        }
    }, [containerRef]);

    useEffect(() => {
        if (props.scrollerContainerId) {
            const listContainer = document.querySelector(`#${props.scrollerContainerId}`);

            listContainer && listContainer.addEventListener('scroll', updateCoords);
            window.addEventListener('resize', updateCoords);

            return () => {
                listContainer && listContainer.removeEventListener('scroll', updateCoords);
                window.removeEventListener('resize', updateCoords);
            };
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent | TouchEvent) => {
            if (showDropDown && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setShowDropDown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showDropDown]);

    useNonInitialEffect(() => {
        switch (availableStaffStatus) {
            case AvailableStaffStatus.FetchSuccess:
                availableStaffByAppointment.get(props.appointmentId) && setLoading(false);
                break;
            case AvailableStaffStatus.Error:
                setLoading(false);
                break;
        }
    }, [availableStaffStatus]);

    useNonInitialEffect(() => {
        switch (bookingsStatus) {
            case BookingsStatus.AssignStaffSuccess:
                saving && setSaving(false);
                props.onStaffSaved?.();
                break;
            case BookingsStatus.Error:
                setSaving(false);
                break;
        }
    }, [bookingsStatus]);

    const clickHandler = (event: React.MouseEvent) => {
        if (props.editable) {
            dispatch(fetchAvailableStaffThunk(apiClientWrapper, props.appointmentId));
            setLoading(true);
            setShowDropDown(true);
        }
    };

    const selectStaffHandler = (selectedStaff: AvailableStaff | null) => {
        setShowDropDown(false);
        setSaving(true);
        dispatch(assignAppointmentStaffThunk(apiClientWrapper, props.appointmentId, selectedStaff ?? undefined));
    };

    return (
        <div
            ref={containerRef}
            className={styles.container}
            onClick={event => event.stopPropagation()}
        >
            {showDropDown ? (
                <DropDown
                    selectedId={props.member?.id ?? null}
                    selectedAvatar={props.member?.imageUrl ?? null}
                    selectedStaffName={props.member?.name ?? null}
                    availableStaff={availableStaff}
                    x={coords.x}
                    y={coords.y}
                    loading={loading}
                    onClick={selectStaffHandler}
                />
            ) : saving ? (
                <div className={styles.spinnerContainer}>
                    <Spinner size={24} />
                </div>
            ) : (
                <div className={styles.selectedStaffContainer}>
                    <StaffChip
                        name={props.member?.name ?? null}
                        imageUrl={props.member?.imageUrl ?? null}
                        onClickHandler={editable ? clickHandler : undefined}
                    />
                    {!props.member && (
                        <FontAwesomeIcon
                            icon={faExclamationCircle}
                            className={styles.warningIcon}
                            title="Please consider reassigning this appointment to someone with availability."
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default Staff;
